import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, RefreshCw, Mail, Phone, User, ShieldCheck, ExternalLink, Copy, CheckCheck, FileDown } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function AdminLeads() {
  const { user, isAdmin } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const allowed = user ? isAdmin : false;

  const load = async () => {
    setRefreshing(true);
    try {
      const r = await api.get("/admin/leads", { withCredentials: true });
      setLeads(r.data.leads || []);
      // Auto-mark all leads as read on page open — makes the header badge disappear.
      try {
        await api.post("/admin/leads/mark-all-read", null, { withCredentials: true });
        window.dispatchEvent(new Event("leads:read"));
      } catch { /* silent */ }
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Solo per amministratori");
      else toast.error("Errore caricamento richieste");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (allowed) load();
    else setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  const copyContact = (l) => {
    const rows = [
      `Nome: ${l.name || "—"}`,
      `Email: ${l.email || "—"}`,
      l.phone && `Telefono: ${l.phone}`,
      `Ruolo: ${l.role || "—"}`,
      l.case_document_type && `Tipo documento: ${l.case_document_type}`,
      l.case_availability && `Disponibilità: ${l.case_availability}`,
      l.case_phase && `Fase: ${l.case_phase}`,
      l.case_signature_status && `Sottoscrizione: ${l.case_signature_status}`,
      l.message && `Messaggio: ${l.message}`,
      l.case_id && `Fascicolo: ${window.location.origin}/case/${l.case_id}`,
    ].filter(Boolean).join("\n");
    navigator.clipboard.writeText(rows).then(() => toast.success("Contatto copiato"));
  };

  const downloadFullPdf = async (caseId, audience = "avvocato") => {
    try {
      const res = await fetch(`${API}/admin/cases/${caseId}/fascicolo-pdf?audience=${audience}`, {
        credentials: "include",
      });
      if (res.status === 401 || res.status === 403) { toast.error("Accedi come amministratore"); return; }
      if (!res.ok) { toast.error("Errore download"); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fascicolo-admin-${audience}-${caseId.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF completo scaricato");
    } catch { toast.error("Errore download"); }
  };

  if (allowed === false) {
    return (
      <div className="min-h-screen bg-paper flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center px-6">
          <div className="text-center max-w-md">
            <ShieldCheck className="text-slate-300 mx-auto" size={40} />
            <h1 className="font-serif text-2xl font-semibold mt-4">Accesso riservato</h1>
            <p className="text-sm text-slate-600 mt-2">Sezione disponibile solo agli amministratori.</p>
            <Link to="/" className="inline-block mt-4 text-xs border hairline hover:border-navy px-4 py-2 rounded-sm">Torna alla home</Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-paper flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto px-6 py-10 w-full">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <div className="overline mb-1">Area amministratore</div>
            <h1 className="font-serif text-3xl lg:text-4xl font-semibold tracking-tight">Richieste dei clienti</h1>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Tutti i contatti inseriti da <b>privati e avvocati</b> al termine del wizard, con il
              collegamento al fascicolo generato. Copia i dati con un click e richiamali per fissare la consulenza.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-mono text-slate-500" data-testid="leads-total">{leads.length} richieste</span>
            <button onClick={async () => {
              try {
                await api.post("/admin/leads/mark-all-read", null, { withCredentials: true });
                setLeads((s) => s.map((l) => ({ ...l, notified: true })));
                toast.success("Tutte segnate come lette");
              } catch { toast.error("Errore"); }
            }} className="inline-flex items-center gap-2 border hairline hover:border-navy px-4 py-2 rounded-sm text-sm"
              data-testid="leads-mark-all">
              <CheckCheck size={14} /> Segna tutte lette
            </button>
            <button onClick={load} disabled={refreshing}
              className="inline-flex items-center gap-2 border hairline hover:border-navy px-4 py-2 rounded-sm text-sm disabled:opacity-60"
              data-testid="leads-refresh">
              {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Aggiorna
            </button>
          </div>
        </div>

        <div className="border hairline bg-white overflow-hidden" data-testid="leads-container">
          {loading ? (
            <div className="p-12 text-center"><Loader2 className="animate-spin text-navy mx-auto" /></div>
          ) : leads.length === 0 ? (
            <div className="p-12 text-center text-slate-500 text-sm" data-testid="leads-empty">
              Nessuna richiesta di consulenza ricevuta.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy text-white text-xs uppercase tracking-wider">
                  <th className="px-3 py-3 text-left">Data</th>
                  <th className="px-3 py-3 text-left">Contatto</th>
                  <th className="px-3 py-3 text-left">Ruolo</th>
                  <th className="px-3 py-3 text-left">Caso</th>
                  <th className="px-3 py-3 text-left">Messaggio</th>
                  <th className="px-3 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className={`border-t hairline hover:bg-slate-50 ${!l.notified ? "bg-forensic-amber/5" : ""}`} data-testid={`lead-${l.id}`}>
                    <td className="px-3 py-3 font-mono text-xs text-slate-600 align-top">
                      <div className="flex items-center gap-2">
                        {!l.notified && <span className="w-2 h-2 rounded-full bg-forensic-red" title="Non letta" />}
                        {l.created_at ? new Date(l.created_at).toLocaleString("it-IT") : "—"}
                      </div>
                    </td>
                    <td className="px-3 py-3 align-top">
                      <div className="flex items-center gap-2 font-medium">
                        <User size={12} className="text-slate-400" />
                        {l.name || <span className="text-slate-400">—</span>}
                      </div>
                      {l.email && (
                        <a href={`mailto:${l.email}`} className="flex items-center gap-2 text-xs text-navy hover:underline mt-1 font-mono">
                          <Mail size={11} /> {l.email}
                        </a>
                      )}
                      {l.phone && (
                        <a href={`tel:${l.phone}`} className="flex items-center gap-2 text-xs text-navy hover:underline mt-1 font-mono">
                          <Phone size={11} /> {l.phone}
                        </a>
                      )}
                    </td>
                    <td className="px-3 py-3 align-top">
                      <span className={`px-2 py-0.5 rounded-sm text-xs uppercase tracking-wider border ${l.role === "avvocato" ? "border-navy text-navy" : "border-slate-300 text-slate-600"}`}>
                        {l.role || "privato"}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-top text-xs">
                      {l.case_document_type && <div><b>{l.case_document_type}</b></div>}
                      {l.case_availability && <div className="text-slate-600">{l.case_availability}</div>}
                      {l.case_phase && <div className="text-slate-500 uppercase tracking-wider text-[10px]">{l.case_phase}</div>}
                    </td>
                    <td className="px-3 py-3 align-top text-xs max-w-xs">
                      <div className="line-clamp-3 text-slate-700">{l.message || <span className="text-slate-400">—</span>}</div>
                    </td>
                    <td className="px-3 py-3 align-top text-right whitespace-nowrap">
                      <div className="flex flex-col gap-1 items-end">
                        {l.case_id && (
                          <Link to={`/case/${l.case_id}`} target="_blank"
                            className="inline-flex items-center gap-1 text-xs border hairline hover:border-navy px-2 py-1 rounded-sm">
                            <ExternalLink size={11} /> Fascicolo
                          </Link>
                        )}
                        {l.case_id && (
                          <button onClick={() => downloadFullPdf(l.case_id, l.role === "avvocato" ? "avvocato" : "privato")}
                            className="inline-flex items-center gap-1 text-xs bg-navy hover:bg-navy-dark text-white px-2 py-1 rounded-sm"
                            data-testid={`lead-pdf-${l.id}`} title="Download gratuito del fascicolo completo">
                            <FileDown size={11} /> PDF completo
                          </button>
                        )}
                        <button onClick={() => copyContact(l)}
                          className="inline-flex items-center gap-1 text-xs border hairline hover:border-navy px-2 py-1 rounded-sm"
                          data-testid={`lead-copy-${l.id}`}>
                          <Copy size={11} /> Copia
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
